import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationContext,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  fetchWithTimeout,
  formatError,
  isRecord,
  result,
  validation,
  validationError,
} from './shared.ts';

const quicEndpoint = 'https://http3.drizt-freak.duckdns.org:8443/';
const maximumAttempts = 5;
const maximumInspectionFailures = 5;
const inspectionPollMilliseconds = 250;
const inspectionTimeoutMilliseconds = 180000;
const inspectionIdPattern =
  /<meta\s+name="quic-inspection-id"\s+content="([0-9a-f-]{36})"\s*>/i;

function inspectionId(html: string): string {
  const match = inspectionIdPattern.exec(html);
  if (!match?.[1]) {
    throw new Error('QUIC probe page did not contain an inspection UUID');
  }
  return match[1];
}

function isHttp3Result(rawData: RawData): boolean {
  if (!isRecord(rawData.protocol)) {
    return false;
  }
  return String(rawData.protocol.http ?? '')
    .toLowerCase()
    .startsWith('http/3');
}

async function requestProbe(attempt: number): Promise<string> {
  const url = new URL(quicEndpoint);
  url.searchParams.set('quicProbe', String(attempt));
  const response = await fetchWithTimeout(
    url,
    { cache: 'no-store' },
    5000,
    'QUIC probe page timed out after 5 seconds',
  );
  if (!response.ok) {
    throw new Error(`QUIC probe page returned ${response.status}`);
  }
  return response.text();
}

async function requestInspection(id: string): Promise<RawData> {
  const url = new URL(`results/${id}`, quicEndpoint);
  const deadline = Date.now() + inspectionTimeoutMilliseconds;
  let failures = 0;
  let lastError: unknown;
  while (Date.now() < deadline) {
    try {
      const response = await fetchWithTimeout(
        url,
        { cache: 'no-store' },
        5000,
        'QUIC inspection request timed out after 5 seconds',
      );
      if (response.status == 202) {
        await response.text();
        await inspectionPollDelay();
        continue;
      }
      if (!response.ok) {
        throw new Error(`QUIC inspection result returned ${response.status}`);
      }
      const body: unknown = await response.json();
      if (!isRecord(body)) {
        throw new Error(
          'QUIC inspection endpoint returned an invalid response',
        );
      }
      return body;
    } catch (error) {
      failures += 1;
      lastError = error;
      if (failures >= maximumInspectionFailures) {
        throw error;
      }
      await inspectionPollDelay();
    }
  }
  throw new Error(
    lastError == null
      ? 'QUIC packet-size detection timed out after 180 seconds'
      : `QUIC packet-size detection timed out: ${errorText(lastError)}`,
  );
}

async function inspectionPollDelay(): Promise<void> {
  await new Promise<void>((resolve) => {
    setTimeout(resolve, inspectionPollMilliseconds);
  });
}

async function collect(): Promise<RawData> {
  if (!window.isSecureContext) {
    throw new Error('QUIC test requires a secure context');
  }

  let latest: RawData = {};
  for (let attempt = 0; attempt < maximumAttempts; attempt += 1) {
    const id = inspectionId(await requestProbe(attempt));
    latest = await requestInspection(id);
    if (isHttp3Result(latest)) {
      break;
    }
  }
  return latest;
}

function packetSize(
  rawData: RawData,
  field:
    | 'incomingMaximumObservedBytes'
    | 'outgoingMaximumConfirmedBytes'
    | 'outgoingPeerMaximumBytes'
    | 'outgoingProbeMaximumBytes',
): number {
  const sizes = isRecord(rawData.packetSize) ? rawData.packetSize : {};
  return typeof sizes[field] == 'number' ? sizes[field] : 0;
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error || !isHttp3Result(rawData)) {
    return 'QUIC failed';
  }
  return `${packetSize(rawData, 'incomingMaximumObservedBytes')} B in / ${packetSize(rawData, 'outgoingMaximumConfirmedBytes')} B out`;
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function webRtcRemoteConnectionFailed(context: ValidationContext): boolean {
  const webRtc = context.results.webrtcRemoteFunctionality?.rawData;
  if (!webRtc) {
    return false;
  }
  return (
    !isRecord(webRtc.dataChannel) ||
    webRtc.dataChannel.received != 'abc-test-ping'
  );
}

function expectedConnectionFailure(): ValidationResult {
  return {
    valid: true,
    reason: null,
    note: 'QUIC connection failure is expected because the WebRTC remote connection also failed',
  };
}

function validate(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return webRtcRemoteConnectionFailed(context)
      ? expectedConnectionFailure()
      : error;
  }
  const reasons: string[] = [];
  if (!isHttp3Result(rawData)) {
    if (webRtcRemoteConnectionFailed(context)) {
      return expectedConnectionFailure();
    }
    reasons.push('The probe page did not load over HTTP/3');
    return validation(reasons);
  }
  if (!isRecord(rawData.protocol) || !rawData.protocol.quic) {
    reasons.push('QUIC version is missing');
  }
  if (packetSize(rawData, 'incomingMaximumObservedBytes') < 1) {
    reasons.push('Maximum incoming packet size is missing');
  }
  const confirmedOutgoing = packetSize(
    rawData,
    'outgoingMaximumConfirmedBytes',
  );
  const probeMaximum = packetSize(rawData, 'outgoingProbeMaximumBytes');
  const peerMaximum = packetSize(rawData, 'outgoingPeerMaximumBytes');
  if (
    confirmedOutgoing < 1200 ||
    probeMaximum != 1452 ||
    peerMaximum < confirmedOutgoing
  ) {
    reasons.push('The outgoing packet-size detection result is invalid');
  }
  if (!isRecord(rawData.traffic)) {
    reasons.push('QUIC traffic totals are missing');
  }
  if (!isRecord(rawData.network)) {
    reasons.push('QUIC network statistics are missing');
  }
  return validation(reasons);
}

export const quicConnectionTest: TestDefinition = {
  id: 'quicConnection',
  title: 'QUIC connection',
  category: 'variableProfile',
  format,
  run,
  validate,
};
